import { CommerceAPI } from "commerce-api"
import { StoreModel } from "./utils"
import { Status, StatusMap } from "../types/commerce"
import { RootStore } from "./RootStore"
import { makeObservable, observable, action, flow, reaction } from "mobx"
import { getAppOrigin } from '@salesforce/pwa-kit-react-sdk/utils/url'
import { StoreDetailResponseItem } from "../types/store/storeDetails"
import { BookRapidDeliveryResponse, RapidDeliveryStoreInitialState } from '../types/store/rapidDelivery'

export class RapidDeliveryStore implements StoreModel {
  api: CommerceAPI
  status: Status
  rootStore: RootStore
  currentDeliveryStore: StoreDetailResponseItem | null
  isRapidDeliveryBooked: boolean
  isRapidDeliveryAvailableForPostcode: boolean

  constructor(rootStore: RootStore, initialState?: RapidDeliveryStoreInitialState) {
    this.api = rootStore.api
    this.status = initialState?.status ?? StatusMap.IDLE
    this.currentDeliveryStore = initialState?.currentDeliveryStore || null
    this.isRapidDeliveryBooked = initialState?.isRapidDeliveryBooked || false
    this.isRapidDeliveryAvailableForPostcode = initialState?.isRapidDeliveryAvailableForPostcode || false
    this.rootStore = rootStore

    makeObservable(this, {
      status: observable,
      currentDeliveryStore: observable,
      isRapidDeliveryBooked: observable,
      isRapidDeliveryAvailableForPostcode: observable,
      setRapidDeliveryAvailability: action.bound,
      setCurrentDeliveryStore: action.bound,
      bookRapidDelivery: flow.bound,
      cancelRapidDelivery: flow.bound
    })
    
    // On load, check if the current basket has rapid delivery selected
    reaction(
      () => (this.rootStore.basketStore.basket)?.c_isRapidDeliverySelected,
      (isSelected) => {
        this.isRapidDeliveryBooked = Boolean(isSelected)
      }
    )
  }

  setRapidDeliveryAvailability(value: boolean) {
    this.isRapidDeliveryAvailableForPostcode = value
  }

  setCurrentDeliveryStore(store: StoreDetailResponseItem | null) {
    this.currentDeliveryStore = store
  }

  *bookRapidDelivery() {
    const response: Response = yield fetch(
      `${getAppOrigin()}/mobify/proxy/ocapi/on/demandware.store/Sites-icelandfoodsuk-Site/default/Rapid-BookDelivery`,
      {
        method: 'POST',
        body: JSON.stringify({ rapidDeliverySelected: true }),
        credentials: 'include'
      }
    )
    const result: BookRapidDeliveryResponse = yield response.json()
    if (!result.success) {
      throw new Error('Failed to book rapid delivery')
    }
    this.isRapidDeliveryBooked = Boolean(result.isSelected)
  }

  *cancelRapidDelivery() {
    const response: Response = yield fetch(
      `${getAppOrigin()}/mobify/proxy/ocapi/on/demandware.store/Sites-icelandfoodsuk-Site/default/Rapid-BookDelivery`,
      {
        method: 'POST',
        body: JSON.stringify({ rapidDeliverySelected: false }),
        credentials: 'include'
      }
    )
    const result: BookRapidDeliveryResponse = yield response.json()
    if (!result.success) {
      throw new Error('Failed to cancel rapid delivery')
    }
    this.isRapidDeliveryBooked = Boolean(result.isSelected)
  }

  get asJson() {
    return {
      status: this.status,
      currentDeliveryStore: this.currentDeliveryStore,
      isRapidDeliveryBooked: this.isRapidDeliveryBooked,
      isRapidDeliveryAvailableForPostcode: this.isRapidDeliveryAvailableForPostcode
    }
  }

  get isRapidDeliveryAvailable() {
    return this.rootStore.globalStore.customSitePreferences?.enableRapidDelivery as boolean
  }

  get rapidDeliveryMaxItems() {
    return this.rootStore.globalStore.customSitePreferences?.rapidDeliveryMaxItems as number
  }

  get rapidDeliverySoftReserveTimeLimit() {
    return this.rootStore.globalStore.customSitePreferences?.rapidDeliverySoftReserveTime as number
  }

  get rapidDeliveryFallback() {
    return this.rootStore.globalStore.customSitePreferences?.rapidDeliveryFallback as string
  }

  get rapidDeliveryServiceUnavailableMessage() {
    return this.rootStore.globalStore.customSitePreferences?.rapidDeliveryServiceUnavailable as boolean
  }

  get isRapidDeliveryEnabledForStore() {
    return this.currentDeliveryStore?.c_rapidDeliveryEnabledForStore ?? false as boolean
  }
}